import { DEFAULT_MODULE_KEYS, MODULE_REGISTRY } from '@/config/modules';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { FormField } from '@/components/FormField';
import { plansService, Plan, PlanLimits } from '@/services/plans.service';
import { CreditCard, Gauge, Users as UsersIcon, Send } from 'lucide-react';
import toast from 'react-hot-toast';

const empty: Omit<Plan, '_id'> = {
  planId: '', name: '', displayName: '', monthlyPriceUsd: 0,
  limits: {
    maxUsers: 5, maxContacts: 500, maxCampaignsPerMonth: 10,
    maxMessagesPerMonth: 1000, aiCreditsPerMonth: 50,
    maxWhatsappAccounts: 1, maxFacebookAccounts: 1,
    maxGoogleAdsAccounts: 1, maxTiktokAccounts: 1, maxBotFlows: 5,
  },
  isActive: true, sortOrder: 0, includedModules: DEFAULT_MODULE_KEYS,
};

const LIMIT_GROUPS: { title: string; fields: { key: keyof PlanLimits; label: string }[] }[] = [
  {
    title: 'Account caps',
    fields: [
      { key: 'maxWhatsappAccounts',  label: 'WhatsApp accounts' },
      { key: 'maxFacebookAccounts',  label: 'Facebook accounts' },
      { key: 'maxGoogleAdsAccounts', label: 'Google Ads accounts' },
      { key: 'maxTiktokAccounts',    label: 'TikTok accounts' },
    ],
  },
  {
    title: 'Seats & flows',
    fields: [
      { key: 'maxUsers',    label: 'Users / seats' },
      { key: 'maxBotFlows', label: 'Bot flows' },
    ],
  },
  {
    title: 'Volume / month',
    fields: [
      { key: 'maxContacts',          label: 'Contacts (total)' },
      { key: 'maxCampaignsPerMonth', label: 'Campaigns / month' },
      { key: 'maxMessagesPerMonth',  label: 'Outbound messages / month' },
      { key: 'aiCreditsPerMonth',    label: 'AI credits / month' },
    ],
  },
];

export default function PlanForm() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState<Omit<Plan, '_id'>>(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    plansService.list().then(rows => {
      const p = rows.find(r => r._id === id);
      if (p) {
        setForm({ planId: p.planId, name: p.name, displayName: p.displayName, monthlyPriceUsd: p.monthlyPriceUsd, limits: { ...empty.limits, ...p.limits }, isActive: p.isActive, sortOrder: p.sortOrder, includedModules: p.includedModules ?? empty.includedModules });
      }
    });
  }, [editing, id]);

  const set = <K extends keyof Omit<Plan, '_id' | 'limits'>>(k: K, v: Omit<Plan, '_id' | 'limits'>[K]) => setForm(s => ({ ...s, [k]: v }));
  const setLimit = (k: keyof PlanLimits, v: number) => setForm(s => ({ ...s, limits: { ...s.limits, [k]: v } }));

  const submit = async () => {
    if (!form.planId.trim() || !form.name.trim() || !form.displayName.trim()) { toast.error('Complete the plan name and ID.'); return; }
    if (Object.values(form.limits).some(value => !Number.isInteger(value) || value < 0)) { toast.error('Limits must be whole numbers, zero or above.'); return; }
    setBusy(true);
    try {
      if (editing) await plansService.update(id!, form); else await plansService.create(form);
      toast.success('Saved'); nav('/plans');
    } finally { setBusy(false); }
  };

  const GROUP_ICONS = [<Gauge size={15} key="g" />, <UsersIcon size={15} key="u" />, <Send size={15} key="s" />];

  return (
    <>
      <Topbar
        title={editing ? `Edit ${form.displayName || 'Plan'}` : 'New Plan'}
        breadcrumb={`Customers / Plans / ${editing ? form.displayName || 'Edit' : 'New'}`}
        backTo="/plans"
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav('/plans')}>Cancel</Button>
            <Button disabled={busy} onClick={submit}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Create plan'}</Button>
          </div>
        }
      />
      <div className="grid w-full grid-cols-1 gap-4 p-4 sm:p-6 xl:grid-cols-2">
        <div className="rounded-xl border border-border bg-surface p-5">
          <h3 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold">
            <CreditCard size={15} className="text-primary" /> Basics
          </h3>
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <FormField label="Plan ID (slug — locked after creation)" required>
              <Input value={form.planId} onChange={(e) => set('planId', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))} disabled={editing} placeholder="pro" />
            </FormField>
            <FormField label="Monthly price (USD)" required>
              <Input type="number" min={0} value={form.monthlyPriceUsd} onChange={(e) => set('monthlyPriceUsd', Number(e.target.value))} />
            </FormField>
            <FormField label="Name (internal)" required>
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="pro" />
            </FormField>
            <FormField label="Display name (shown to clients)" required>
              <Input value={form.displayName} onChange={(e) => set('displayName', e.target.value)} placeholder="Pro" />
            </FormField>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-surface p-5">
          <h3 className="text-sm font-semibold">Availability</h3>
          <p className="mt-1 text-xs text-text-muted">Inactive plans stay on existing companies and cannot be assigned to new companies.</p>
          <label className="mt-4 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isActive} onChange={e => set('isActive', e.target.checked)} /> Available for new companies</label>
          <FormField label="Display order"><Input type="number" value={form.sortOrder} onChange={e => set('sortOrder', Number(e.target.value))} /></FormField>
        </div>
        {LIMIT_GROUPS.map((g, gi) => (
          <div key={g.title} className="rounded-xl border border-border bg-surface p-5">
            <h3 className="mb-4 inline-flex items-center gap-2 text-sm font-semibold">
              <span className="text-primary">{GROUP_ICONS[gi]}</span> {g.title}
            </h3>
            <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
              {g.fields.map(f => (
                <FormField key={f.key} label={f.label} required>
                  <Input type="number" min={0} value={form.limits[f.key]} onChange={(e) => setLimit(f.key, Number(e.target.value))} />
                </FormField>
              ))}
            </div>
          </div>
        ))}
        <div className="rounded-xl border border-border bg-surface p-5 xl:col-span-2">
          <h3 className="text-sm font-semibold">Included modules</h3>
          <p className="mt-1 mb-4 text-xs text-text-muted">Defaults for new companies. You can adjust each company's access separately.</p>
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-4">{MODULE_REGISTRY.map(module => <label key={module.key} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2.5 text-xs"><input type="checkbox" checked={form.includedModules?.includes(module.key) ?? !module.explicit} onChange={e => set('includedModules', e.target.checked ? [...(form.includedModules ?? []), module.key] : (form.includedModules ?? []).filter(key => key !== module.key))} /><module.icon size={14} className="text-primary" />{module.label}</label>)}</div>
        </div>
      </div>
    </>
  );
}
